import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  ConfirmAction,
  ConfirmDialog,
  ConfirmDialogData
} from './../../shared/dialogs/confirm/confirm.dialog';
import {
  LoadingDialog,
  LoadingDialogData
} from './../../shared/dialogs/loading/loading.dialog';

@Injectable()
export class DialogService {
  private currentLoadingDialogRef: MatDialogRef<LoadingDialog>;

  private subject: Subject<boolean>;
  private isLoading = false;
  private brandName$ = new BehaviorSubject<string | null>(null);

  setData(brandName: string): void {
    this.brandName$.next(brandName);
  }

  getData(): Observable<string | null> {
    return this.brandName$.asObservable();
  }

  clearData(): void {
    this.brandName$.next(null);
  }

  isLoadingInProgress(): boolean {
    return this.isLoading;
  }
  allowDeactivation(): Observable<boolean> {
    return this.subject.asObservable();
  }

  constructor(
    private dialog: MatDialog,
    // private viewportScroller: ViewportScroller,
    private translateService: TranslateService
  ) {
    this.subject = new Subject<boolean>();
    this.subject.pipe(map(v => (this.isLoading = !v))).subscribe();
  }
  hasActiveDialog(): boolean {
    return this.dialog.openDialogs.length > 0;
  }

  showLoadingDialog(options?: LoadingDialogData) {
    this.subject.next(false);
    // this.isLoading = true;
    this.currentLoadingDialogRef = this.dialog.open(LoadingDialog, {
      data: options,
      closeOnNavigation: false,
      maxWidth: 344
    });
    this.currentLoadingDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.subject.next(true);
        // this.isLoading = false;
        if (
          this.currentLoadingDialogRef.componentInstance.data.scrollToTopOnClose
        ) {
          // Doesn't work in IE11 now??
          // this.viewportScroller.scrollToPosition( [ 0, 0] );
          window.scrollTo(0, 0);
        }
      });
  }

  closeLoadingDialog(ignoreScrollToTopOnClose?: boolean) {
    if (this.currentLoadingDialogRef) {
      if (
        ignoreScrollToTopOnClose &&
        this.currentLoadingDialogRef.componentInstance &&
        this.currentLoadingDialogRef.componentInstance.data
      ) {
        this.currentLoadingDialogRef.componentInstance.data.scrollToTopOnClose = false;
      }
      this.currentLoadingDialogRef.close();
    }
  }

  createAlert(options?: {
    confirmDialogData?: ConfirmDialogData;
    disableClose?: boolean;
  }): MatDialogRef<ConfirmDialog> {
    const maxWidth = options.confirmDialogData.maxWidth || 344;
    const okOnlyData = { okOnly: true };
    const alertData =
      options && options.confirmDialogData
        ? { ...options.confirmDialogData, ...okOnlyData }
        : okOnlyData;
    const dialogRef: MatDialogRef<
      ConfirmDialog,
      ConfirmAction
    > = this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: alertData,
      disableClose: options.disableClose,
      maxWidth
    });
    return dialogRef;
  }

  showConfirm(
    confirmDialogData?: ConfirmDialogData
  ): Observable<ConfirmAction> {
    const maxWidth = confirmDialogData.maxWidth || 344;
    const dialogRef: MatDialogRef<
      ConfirmDialog,
      ConfirmAction
    > = this.dialog.open(ConfirmDialog, {
      closeOnNavigation: false,
      autoFocus: false,
      data: confirmDialogData,
      panelClass: confirmDialogData.panelClass,
      maxWidth
    });
    return dialogRef.afterClosed();
  }

  showUnsavedConfirm(message?: string): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      closeOnNavigation: false,
      autoFocus: false,
      data: {
        message: message || this.translateService.instant('form.leaveWarning')
      },
      maxWidth: 344
    });
    return dialogRef
      .afterClosed()
      .pipe(map((value: ConfirmAction) => value === 'ok'));
  }
  showConfirmAsync(
    confirmDialogData?: ConfirmDialogData
  ): Promise<ConfirmAction> {
    return this.showConfirm(confirmDialogData).toPromise<ConfirmAction>();
  }

  /**
   * Closes all currently open dialogs.
   */
  closeAll() {
    this.dialog.closeAll();
  }

}
